'use client';

import { useEffect, useRef } from 'react';

export interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

const VARIANT_STYLES: Record<'default' | 'danger', { button: string }> = {
  default: {
    button: 'bg-[var(--k-color-accent)] text-white hover:brightness-110',
  },
  danger: {
    button: 'bg-[var(--k-color-danger-action)] text-white hover:bg-[var(--k-color-danger-action-hover)]',
  },
};

export function ConfirmationDialog({
  isOpen,
  title,
  description,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  variant = 'default',
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  useEffect(() => {
    if (isOpen) {
      confirmRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const style = VARIANT_STYLES[variant];

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirmation-title"
      aria-describedby="confirmation-description"
      data-tour="confirmation-dialog"
      className="fixed inset-0 z-[68] flex items-center justify-center bg-slate-900/55 px-4 py-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-md rounded-[var(--k-radius-lg)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] p-6 shadow-[var(--k-shadow-lg)]"
      >
        <h3
          id="confirmation-title"
          className="text-lg font-semibold text-[var(--k-color-text-primary)]"
        >
          {title}
        </h3>
        <p
          id="confirmation-description"
          className="mt-2 text-sm text-[var(--k-color-text-secondary)]"
        >
          {description}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-[var(--k-radius-md)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] px-4 py-2 text-sm font-semibold text-[var(--k-color-text-secondary)] transition hover:border-[var(--k-color-border-strong)] hover:text-[var(--k-color-text-primary)]"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className={`rounded-[var(--k-radius-md)] px-4 py-2 text-sm font-bold shadow-[var(--k-shadow-sm)] transition ${style.button}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
